// client/src/components/RedirectQRCTransfertFid.js

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { sanitize } from '../../utils/sanitize';
import { ensureUserSession } from '../../utils/ensureUserSession';
import ScreenState from '../shared/ScreenState';

/**
 * QR attendu (scan via appareil photo):
 *   https://ton-domaine/redirectqrctransfertfid/<offerId>
 *
 * Comportement:
 *  1) Session existante restaurée (locale ou appareil), sinon création anonyme
 *  2) users/{uid} créé si absent (role='client')
 *  3) Redirection vers la page de claim : /transfertfid?offer=<offerId>&mode=claim
 */

const RedirectQRCTransfertFid = () => {
  const navigate = useNavigate();
  const { offerId: rawOfferFromParam } = useParams();
  const location = useLocation();

  // On accepte <offerId> en segment ET ?offer=... en query
  const query = new URLSearchParams(location.search);
  const rawOfferFromQuery = query.get('offer');

  const rawOffer = rawOfferFromParam || rawOfferFromQuery || '';
  const offerId  = rawOffer ? sanitize(rawOffer) : '';

  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => { setFailed(false); setAttempt((n) => n + 1); }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      if (!offerId) {
        navigate('/clientfid', { replace: true });
        return;
      }

      let user;
      try {
        user = await ensureUserSession({ allowNewAnonymous: true, overrideLogoutGuard: true });
      } catch (error) {
        console.error('[RedirectQRCTransfertFid] session error:', error);
        if (active) setFailed(true);
        return;
      }
      if (!active) return;

      try {
        const userDocRef = doc(firestore, 'users', user.uid);
        const snapshot = await getDoc(userDocRef);
        if (!snapshot.exists()) {
          await setDoc(userDocRef, { uid: user.uid, role: 'client', createdAt: serverTimestamp() }, { merge: true });
        }
      } catch (error) {
        console.warn('[RedirectQRCTransfertFid] user doc:', error?.code || error);
      }

      if (active) navigate(`/transfertfid?offer=${encodeURIComponent(offerId)}&mode=claim`, { replace: true });
    })();

    return () => {
      active = false;
    };
  }, [navigate, offerId, attempt]);

  if (failed) {
    return (
      <ScreenState
        variant="error"
        title="Connexion impossible"
        text="Vérifiez votre connexion internet puis réessayez."
        actionLabel="Réessayer"
        onAction={retry}
      />
    );
  }

  return <ScreenState title="Récupération de l'offre" text="Un instant…" />;
};

export default RedirectQRCTransfertFid;
